import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';
import moment from 'moment';

import TotalModalWrapper from '../../Share/Normal/TotalModalWrapper';
import FetchModule from '../../Share/Network/FetchModule';
import PaymentOptionSettingSelectBox from '../../../MemberRegistration/Model/NewPaymentOptionSettingSelectBox';
import PaymentOptionCustomInput from '../../../MemberRegistration/Component/NewPaymentOptionCustomInput';
import NewColorCustomInput from '../../../MemberRegistration/Component/NewColorCustomInput';
import AddPaymentOptionModal from '../../../MemberRegistration/Model/AddPaymentOptionModal';
import NewProductCountInput from '../../../Component/MemberComponent/NewProductCountInput';

import WhiteTrashIcon from '../../../image/WhiteTrashIcon.png';
import { ReactComponent as LeftArrowSvg } from '../../../image/LeftArrow.svg';

const DefaultPaymentOption = [{ id: 0, name: '현금' }, { id: 0, name: '카드' }, { id: 0, name: '포인트' }];

export default function WaitingProductPaymentModal(props) {
  const { userData, productData, onComplete, closeFn } = props;

  const history = useHistory();

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [selectedProduct, setSelectedProduct] = useState(undefined);
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [count, setCount] = useState(userData.quantity || 1);
  const [discount, setDiscount] = useState(0);
  const [notReceived, setNotReceived] = useState(0);
  const [paymentOptions, setPaymentOptions] = useState(DefaultPaymentOption);
  const [selectedPaymentOptions, setSelectedPaymentOptions] = useState(() => {
    if (userData.point && userData.point > 0) {
      return [{ id: 0, name: '포인트', price: userData.point }, { id: 0, name: '현금', price: 0 }];
    }
    return [{ id: 0, name: '현금', price: 0 }];
  });
  const [isClickedAddPaymentOption, setIsClickedAddPaymentOption] = useState(false);
  const [openPaymentDropdownIndex, setOpenPaymentDropdownIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // 결제수단 데이터 불러오기 //
  useEffect(() => {
    const fetchModule = new FetchModule();
    (async () => {
      const responseData = await fetchModule.getDataVersion2('payment_option/', 'GET');
      if (responseData.status === 200) {
        setPaymentOptions(DefaultPaymentOption.concat(responseData.data.paymentOptions));
      }
      else if (responseData.status === 401) history.push('/login');
    })();
  }, []);

  // 초기 상품 선택: userData.itemName과 일치하는 상품 //
  useEffect(() => {
    if (!productData || productData.length === 0) return;
    const matched = productData.find(p => p.name === userData.itemName);
    if (matched) setSelectedProduct(matched);
  }, [productData, userData]);

  // 미수금 산정 //
  useEffect(() => {
    if (!selectedProduct) { setNotReceived(0); return; }
    let totalPaid = 0;
    for (let i = 0; i < selectedPaymentOptions.length; i++) {
      if (selectedPaymentOptions[i].price !== '') {
        totalPaid += parseInt(selectedPaymentOptions[i].price ?? 0);
      }
    }
    if (discount !== '') totalPaid += parseInt(discount.toString().replaceAll(',', ''));
    setNotReceived(Math.max((selectedProduct.price * count) - totalPaid, 0));
  }, [selectedProduct, count, selectedPaymentOptions, discount]);

  function onChangeSelectedPaymentOption(data, index) {
    const newOptions = selectedPaymentOptions.slice();
    newOptions[index].id = data.id;
    newOptions[index].name = data.name;
    setSelectedPaymentOptions(newOptions);
  }

  function onChangePaymentOptionPrice(value, index) {
    const newOptions = selectedPaymentOptions.slice();
    newOptions[index].price = value.replaceAll(',', '');
    setSelectedPaymentOptions(newOptions);
  }

  function onClickAddPaymentOption() {
    const newOptions = [...selectedPaymentOptions, { id: 0, name: '현금', price: 0 }];
    setSelectedPaymentOptions(newOptions);
  }

  function onClickDeletePaymentOption(index) {
    const newOptions = selectedPaymentOptions.slice();
    newOptions.splice(index, 1);
    setSelectedPaymentOptions(newOptions);
  }

  function onClickSavePaymentOptionModal(optionName) {
    if (optionName === '') { alert(t('waiting.productPaymentModal.alertPaymentOptionNameRequired')); return; }
    if (isLoading) { alert(t('waiting.productPaymentModal.alertSaveInProgress')); return; }
    setIsLoading(true);
    const fetchModule = new FetchModule();
    (async () => {
      const responseData = await fetchModule.postDataVersion2('payment_option/save', 'POST', { optionName });
      if (responseData.status === 200) {
        setPaymentOptions(DefaultPaymentOption.concat(responseData.data.paymentOptions));
        setIsLoading(false);
        setIsClickedAddPaymentOption(false);
      }
      else if (responseData.status === 401) history.push('/login');
      else { alert(responseData.error); setIsLoading(false); }
    })();
  }

  function onClickDeleteOptionFromList(paymentOptionId) {
    if (isLoading) { alert(t('waiting.productPaymentModal.alertDeleteInProgress')); return; }
    setIsLoading(true);
    const fetchModule = new FetchModule();
    (async () => {
      const responseData = await fetchModule.postDataVersion2('payment_option/delete', 'POST', { paymentOptionId });
      if (responseData.status === 200) {
        setPaymentOptions(DefaultPaymentOption.concat(responseData.data.paymentOptions));
        setIsLoading(false);
        setIsClickedAddPaymentOption(false);
      }
      else if (responseData.status === 401) history.push('/login');
      else { alert(responseData.error); setIsLoading(false); }
    })();
  }

  function onClickSave() {
    if (!selectedProduct) { alert(t('waiting.productPaymentModal.alertSelectProduct')); return; }
    if (isLoading) return;

    const storeId = window.sessionStorage.getItem('StoreId');
    let finalCashPrice = 0;
    let finalCardPrice = 0;
    let finalPoint = 0;

    const finalSelectedPaymentOptions = selectedPaymentOptions.filter(
      opt => opt.price !== '' && opt.price !== 0
    );

    for (let i = 0; i < finalSelectedPaymentOptions.length; i++) {
      const price = parseInt(finalSelectedPaymentOptions[i].price.toString().replaceAll(',', ''));
      if (finalSelectedPaymentOptions[i].id === 0) {
        if (finalSelectedPaymentOptions[i].name === '현금') finalCashPrice += price;
        else if (finalSelectedPaymentOptions[i].name === '카드') finalCardPrice += price;
        else if (finalSelectedPaymentOptions[i].name === '포인트') finalPoint += price;
      }
    }

    const discountPrice = parseInt(discount.toString().replaceAll(',', '')) || 0;

    const requestData = {
      id: -1,
      storeId: parseInt(storeId),
      memberId: parseInt(userData.memberId),
      productId: selectedProduct.id,
      merchantUid: userData.merchantUid,
      cardPrice: finalCardPrice,
      cashPrice: finalCashPrice,
      discountPrice: discountPrice,
      notReceivedPrice: notReceived,
      point: finalPoint,
      count: count,
      approvalDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
      productPaymentOptions: finalSelectedPaymentOptions,
    };

    setIsLoading(true);
    const fetchModule = new FetchModule();
    (async () => {
      const responseData = await fetchModule.postDataVersion2('product/usage/approve/' + userData.merchantUid, 'POST', requestData);
      if (responseData.status === 200) {
        alert(t('waiting.productPaymentModal.alertPurchaseComplete'));
        setIsLoading(false);
        if (onComplete) onComplete();
      }
      else if (responseData.status === 401) {
        alert(t('waiting.productPaymentModal.alertLoginRequired'));
        history.push('/login');
      }
      else if (responseData.status === 409) {
        // 포인트 잔액 부족 / 재고 부족 등 서버측 사용 가능 여부 충돌 //
        alert(responseData.error);
        setIsLoading(false);
        return;
      }
      else {
        setIsLoading(false);
      }
    })();
  }

  if (!userData) return null;

  const productPicture = selectedProduct
    ? (selectedProduct.picture || 'https://picture.link-zone.org/DefaultLogo.png')
    : (userData.itemPicture || 'https://picture.link-zone.org/DefaultLogo.png');

  const productPrice = selectedProduct ? (selectedProduct.price * count).toLocaleString('ko-KR') : '0';

  return (
    <TotalModalWrapper>
      {isClickedAddPaymentOption && (
        <AddPaymentOptionModal
          onClickSave={onClickSavePaymentOptionModal}
          onClickExit={() => setIsClickedAddPaymentOption(false)} />
      )}
      <ModalView>
        <HeaderRow>
          <HeaderTextWrapper>
            <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>{userData.userName} - {t('waiting.productPaymentModal.titleSuffix')}</Text>
          </HeaderTextWrapper>
        </HeaderRow>
        <ProductRow>
          <ProductImage src={productPicture} alt='product' />
          <ProductSelectWrapper onClick={() => setIsProductDropdownOpen(prev => !prev)}>
            <ProductSelectTextWrapper>
              <Text
                fontFamily='NotoSansKR-Regular'
                fontSize={14}
                color={selectedProduct ? theme.text : theme.subText}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {selectedProduct ? selectedProduct.name : t('waiting.productPaymentModal.selectProduct')}
              </Text>
            </ProductSelectTextWrapper>
            <DropdownArrow isOpen={isProductDropdownOpen}>
              <StyledDownArrowSvg isOpen={isProductDropdownOpen} />
            </DropdownArrow>
            {isProductDropdownOpen && (
              <DropdownList>
                {productData && productData.map((p, idx) => (
                  <DropdownItem
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProduct(p);
                      setIsProductDropdownOpen(false);
                    }}>
                    {p.picture !== '' && <DropdownItemImage src={p.picture} alt={p.name} />}
                    <Text
                      fontFamily='NotoSansKR-Regular'
                      fontSize={14}
                      color={theme.text}
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                    </Text>
                  </DropdownItem>
                ))}
              </DropdownList>
            )}
          </ProductSelectWrapper>
        </ProductRow>
        <InfoRow>
          <InfoSection>
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('waiting.productPaymentModal.purchaseCount')}</Text>
            <NewProductCountInput value={count} setValue={setCount}/>
          </InfoSection>
          <InfoSection>
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('waiting.productPaymentModal.productPrice')}</Text>
            <PriceReadonlyWrapper>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', lineHeight: '17px' }}>{productPrice}</Text>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText} style={{ lineHeight: '17px' }}>{t('waiting.productPaymentModal.wonUnit')}</Text>
            </PriceReadonlyWrapper>
          </InfoSection>
        </InfoRow>
        {selectedPaymentOptions.map((opt, index) => (
          <PaymentRow key={'payment-' + index} style={{ zIndex: selectedPaymentOptions.length - index }}>
            <InfoSection>
              <InfoLabelRow>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('waiting.productPaymentModal.paymentMethod')}{index > 0 ? ` ${index + 1}` : ''}</Text>
                {index > 0 && (
                  <DeleteButton onClick={() => onClickDeletePaymentOption(index)}>
                    <DeleteIcon src={WhiteTrashIcon} />
                  </DeleteButton>
                )}
              </InfoLabelRow>
              <PaymentOptionSettingSelectBox
                index={index}
                value={opt}
                setValue={onChangeSelectedPaymentOption}
                options={paymentOptions}
                disabled={paymentOptions.length === 0}
                width={111}
                onClickAdditionOption={() => setIsClickedAddPaymentOption(true)}
                onClickDeleteOption={onClickDeleteOptionFromList} />
            </InfoSection>
            <InfoSection>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('waiting.productPaymentModal.receivedPrice')}</Text>
              <UnitInputWrapper>
                <PaymentOptionCustomInput
                  index={index}
                  value={(opt.price ?? 0).toLocaleString('ko-KR')}
                  setValue={onChangePaymentOptionPrice}
                  placeholder='0'
                  disabled={false}
                  textAlign='right' />
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText} style={{ lineHeight: '17px' }}>{t('waiting.productPaymentModal.wonUnit')}</Text>
              </UnitInputWrapper>
            </InfoSection>
          </PaymentRow>
        ))}
        <AddPaymentButton onClick={onClickAddPaymentOption}>{t('waiting.productPaymentModal.addPaymentMethod')}</AddPaymentButton>
        <InfoRow>
          <InfoSection>
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('waiting.productPaymentModal.discountAmount')}</Text>
            <UnitInputWrapper>
              <NewColorCustomInput
                value={discount.toLocaleString('ko-KR')}
                setValue={setDiscount}
                placeholder='0'
                disabled={false}
                textAlign='right'
                color={theme.subKey} />
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText} style={{ lineHeight: '17px' }}>{t('waiting.productPaymentModal.wonUnit')}</Text>
            </UnitInputWrapper>
          </InfoSection>
          <InfoSection>
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('waiting.productPaymentModal.notReceived')}</Text>
            <PriceReadonlyWrapper>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.pointRed} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', lineHeight: '17px' }}>{notReceived.toLocaleString('ko-KR')}</Text>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText} style={{ lineHeight: '17px' }}>{t('waiting.productPaymentModal.wonUnit')}</Text>
            </PriceReadonlyWrapper>
          </InfoSection>
        </InfoRow>
        <FooterRow>
          <CancelButton onClick={closeFn}>{t('waiting.productPaymentModal.cancel')}</CancelButton>
          <SaveButton onClick={onClickSave} disabled={isLoading}>{t('waiting.productPaymentModal.save')}</SaveButton>
        </FooterRow>
      </ModalView>
    </TotalModalWrapper>
  );
}

const ModalView = styled.div`
  display: flex;
  flex-direction: column;

  padding: 20px;

  width: 430px;

  gap: 10px;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  margin-bottom: 20px;
`;

const HeaderTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledDownArrowSvg = styled(LeftArrowSvg)`
  width: 10px;
  height: auto;

  color: ${props => props.theme.subText};

  transform: ${props => props.isOpen ? 'rotate(90deg)' : 'rotate(-90deg)'};
  transition: transform 0.2s ease-in-out;
`;

const ProductRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;

  gap: 10px;
`;

const ProductImage = styled.img`
  width: 50px;
  height: 50px;

  border-radius: 10px;
`;

const ProductSelectWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 5px 10px;

  width: 100%;

  border: 1px solid ${props => props.theme.line};
  border-radius: 8px;
  background-color: ${props => props.theme.subBackground};

  cursor: pointer;
`;

const ProductSelectTextWrapper = styled.div`
  display: flex;

  justify-content: flex-start;
  align-items: center;

  height: 40px;
`;

const DropdownArrow = styled.span`
  display: flex;

  align-items: center;

  margin-left: 8px;
`;

const DropdownList = styled.div`
  position: absolute;

  top: calc(100% + 4px);
  left: 0;

  width: 100%;
  max-height: 200px;

  border: 1px solid ${props => props.theme.line};
  border-radius: 8px;
  background-color: ${props => props.theme.subBackground};

  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  overflow-y: auto;

  z-index: 100;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.scrollBar};
    border-radius: 200px;
  }

  &::-webkit-scrollbar-track {
    background-color: ${props => props.theme.subackground};
    border-radius: 200px;
  }
`;

const DropdownItem = styled.div`
  display: flex;

  flex-direction: row;
  align-items: center;

  padding: 8px 12px;

  gap: 8px;

  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.background};
  }
`;

const DropdownItemImage = styled.img`
  width: 50px;
  height: 50px;

  border-radius: 10x;
  object-fit: cover;

  flex-shrink: 0;
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 10px 0 10px 0;

  width: 100%;

  gap: 10px;
`;

const PaymentRow = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;

  padding: 10px 0 10px 0;
  justify-content: space-between;
  align-items: center;

  width: 100%;

  gap: 10px;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;

  gap: 6px;

  width: 100%;
`;

const InfoLabelRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  gap: 8px;
`;

const PriceReadonlyWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  padding: 10px;

  height: 50px;

  gap: 5px;

  box-sizing: border-box;
`;

const UnitInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
`;

const AddPaymentButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 40px;

  border-radius: 20px;
  background-color: ${props => props.theme.buttonFill};

  font-family: NotoSansKR-Bold;
  font-size: 14px;
  color: ${props => props.theme.subText};

  cursor: pointer;
  transition: all 0.3s ease-in-out;

  &:hover {
    color: ${props => props.theme.white};
    background-color: ${props => props.theme.subKey};
  }
`;

const DeleteButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 20px;
  height: 20px;

  border-radius: 2px;
  background-color: ${props => props.theme.pointRed}80;

  cursor: pointer;
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: ${props => props.theme.pointRed};
  }
`;

const DeleteIcon = styled.img`
  width: 14px;
  height: 14px;
`;

const FooterRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  gap: 10px;
`;

const CancelButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 0 27px 0 27px;

  height: 40px;

  border-radius: 10px;
  background-color: ${props => props.theme.buttonFill};

  font-family: NotoSansKR-Medium;
  font-size: 14px;
  color: ${props => props.theme.text};

  cursor: pointer;
  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      background-color: ${props => props.theme.line};
    }
  }
`;

const SaveButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex: 1;
  height: 40px;

  border-radius: 10px;
  background-color: ${props => props.disabled ? props.theme.line : props.theme.subKey};

  font-family: NotoSansKR-Medium;
  font-size: 14px;
  color: ${props => props.theme.background};

  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  transition: all 0.3s ease-in-out;

  @media (hover: hover) {
    &:hover {
      background-color: ${props => props.disabled ? props.theme.line : props.theme.subKeyHover};
    }
  }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0 0 0 0;

  color: ${props => props.color};

  &:hover {
    cursor: ${props => props.cursor};
  }

  transition: all 0.3s ease-in-out;
`;
